<template>
  <div class="rc-table" role="table" :aria-label="t('route_cards.table_label')">
    <div class="rc-table__scroll">
      <div class="rc-table__inner">
        <div class="rc-table__row rc-table__row--head" role="row">
          <div role="columnheader">{{ t('route_cards.columns.section') }}</div>
          <div role="columnheader">{{ t('route_cards.columns.to_point') }}</div>
          <div role="columnheader">{{ t('route_cards.columns.grid_ref') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.height') }}</div>
          <div role="columnheader">{{ t('route_cards.columns.bearing') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.km') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.total') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.ascent') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.descent') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.time') }}</div>
          <div role="columnheader" class="num">{{ t('route_cards.columns.eta') }}</div>
        </div>

        <div v-if="start" class="rc-table__row" role="row">
          <div><span class="rc-table__start">0</span></div>
          <div class="rc-table__name">
            {{ pointName(start) }} <span class="rc-table__muted">{{ t('route_cards.start_label') }}</span>
          </div>
          <div>{{ start.gridRef }}</div>
          <div class="num">{{ heightOf(start) }}</div>
          <div class="rc-table__muted">—</div>
          <div></div>
          <div class="num rc-table__muted">0.0</div>
          <div></div><div></div><div></div>
          <div class="num rc-table__strong">{{ formatClock(start.leaveMinutes) }}</div>
        </div>

        <div
          v-for="leg in card.legs"
          :key="leg.label"
          class="rc-table__leg"
          :class="{ 'rc-table__leg--selected': leg.toIndex === selected }"
        >
          <button
            type="button"
            class="rc-table__row rc-table__row--button"
            role="row"
            :aria-pressed="leg.toIndex === selected"
            @click="emit('select', leg.toIndex)"
          >
            <div><span class="rc-table__chip">{{ leg.label }}</span></div>
            <div class="rc-table__name">
              <span>{{ pointName(card.points[leg.toIndex]) }}</span>
              <span v-if="leg.toIndex === card.points.length - 1" class="rc-table__muted">
                {{ t('route_cards.finish_label') }}
              </span>
              <span class="rc-table__from">
                {{ t('route_cards.from_point', { name: pointName(card.points[leg.fromIndex]) }) }}
              </span>
            </div>
            <div>{{ card.points[leg.toIndex]?.gridRef }}</div>
            <div class="num">{{ heightOf(card.points[leg.toIndex]) }}</div>
            <div class="rc-table__strong">{{ formatBearing(leg.gridBearing) }} / {{ formatBearing(leg.magneticBearing) }}</div>
            <div class="num">{{ formatKm(leg.distanceMeters) }}</div>
            <div class="num rc-table__muted">{{ formatKm(leg.cumulativeDistanceMeters) }}</div>
            <div class="num">{{ leg.ascentMeters }}</div>
            <div class="num">{{ leg.descentMeters }}</div>
            <div class="num">{{ formatDuration(leg.movingMinutes) }}</div>
            <div class="num rc-table__strong">{{ formatClock(leg.arriveMinutes) }}</div>
          </button>

          <div v-if="card.points[leg.toIndex]?.note" class="rc-table__extra">
            <span class="rc-table__label">{{ t('route_cards.note_label') }}</span>
            <span>{{ card.points[leg.toIndex].note }}</span>
          </div>
          <div v-if="card.points[leg.toIndex]?.escape" class="rc-table__extra">
            <span class="rc-table__label rc-table__label--escape">{{ t('route_cards.escape_label') }}</span>
            <span>{{ card.points[leg.toIndex].escape }}</span>
          </div>
          <div v-if="restOf(leg) > 0" class="rc-table__rest">
            {{ t('route_cards.rest_summary', { duration: formatDuration(restOf(leg)), time: formatClock(leg.leaveMinutes) }) }}
          </div>

          <slot v-if="leg.toIndex === selected" name="editor" :point-index="leg.toIndex"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBearing, formatClock, formatDuration, formatKm } from '../../utils/route_cards/format';
import type { RouteCard, RouteCardLeg, RouteCardPoint } from './types';

// The leg-by-leg table: one row per section, labelled the same as the map.
// Purely presentational — selecting a row is reported up, and the selected
// row's editor is slotted in by the parent.
const props = defineProps<{ card: RouteCard; selected: number }>();
const emit = defineEmits<{ (event: 'select', index: number): void }>();
const { t } = useI18n();

const start = computed(() => props.card.points[0]);

function pointName(point: RouteCardPoint | undefined): string {
  if (!point) return '';
  return point.name || t('route_cards.point_fallback', { number: point.index });
}

function heightOf(point: RouteCardPoint | undefined): string {
  if (!point || point.elevationMeters === null) return '';
  return t('route_cards.metres', { value: point.elevationMeters });
}

function restOf(leg: RouteCardLeg): number {
  return leg.leaveMinutes - leg.arriveMinutes;
}
</script>

<style lang="scss">
$columns: 58px minmax(190px, 1fr) 92px 64px 108px 50px 56px 52px 52px 52px 58px;

.rc-table {
  background: #22242A;
  border-radius: 12px;
  overflow: hidden;
}

.rc-table__scroll { overflow-x: auto; }
.rc-table__inner { min-width: 940px; }

.rc-table__row {
  display: grid;
  grid-template-columns: $columns;
  gap: 0 8px;
  align-items: center;
  padding: 12px 16px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: #FFFFFF;
  border-bottom: 1px solid #2D2F35;
  text-align: left;

  &--head {
    font-size: 11px;
    font-weight: 700;
    color: #ACB0BF;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom-color: #434343;
  }

  &--button {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: none;
    font-family: inherit;
    cursor: pointer;

    &:focus-visible { outline: 2px solid #FDDF00; outline-offset: -2px; }
  }

  .num { text-align: right; }
}

.rc-table__leg {
  border-bottom: 1px solid #2D2F35;

  &--selected { background: rgba(253, 223, 0, 0.07); }
}

.rc-table__chip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 7px;
  border-radius: 100px;
  border: 1px solid #505259;
  background: #17181D;
  color: #FDDF00;
  font-size: 11px;
  font-weight: 800;

  .rc-table__leg--selected & {
    background: #FDDF00;
    border-color: #FDDF00;
    color: #17181D;
  }
}

.rc-table__start {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #4BC263;
  color: #17181D;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rc-table__name {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 6px;
  font-weight: 600;
  min-width: 0;
}

.rc-table__from {
  flex-basis: 100%;
  font-size: 11px;
  font-weight: 500;
  color: #ACB0BF;
}

.rc-table__muted { color: #ACB0BF; font-weight: 500; }
.rc-table__strong { font-weight: 700; }

.rc-table__extra {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 0 16px 12px 82px;
  font-size: 12px;
  color: #D9D9D9;
}

.rc-table__label {
  font-weight: 700;
  color: #FFFFFF;

  &--escape { color: #F57700; }
}

.rc-table__rest {
  margin: 0 16px 12px 82px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #2D2F35;
  font-size: 12px;
  color: #D9D9D9;
}
</style>
